import { getApiOrigin } from "@utils/apiOrigin";
import { authenticatedFetch } from "@utils/authenticatedFetch";

/**
 * Rules service proxies to the aggregator:
 * {AGGREGATOR}/api/v1/som-sections/query?tag=…&country_code=…&industry=…
 * Hub calls POST /api/v1/som-tags/query (same data; tag normalization on server).
 */
const SOM_TAGS_QUERY_ENDPOINT = "/api/v1/som-tags/query";

/** Aggregator/rules payload for numeric LSC K-tags (Form CMS-2786R), not SOM appendix rows. */
export interface SomTagGuidelineCmsForm {
  guidance_source?: string;
  document_type?: string;
  form_id?: string;
  title?: string;
  tag?: string;
  pdf_search_query?: string;
  cms_official_url?: string | null;
  cms_som_pdf_url?: string | null;
  pdf_page_hint?: number | null;
  pdf_open_fragment?: string | null;
  file_path?: string | null;
}

export interface LscKTagGuidelineEntry {
  requested_tag?: string;
  canonical_tag?: string;
  tag_guideline: SomTagGuidelineCmsForm;
}

export interface SomQuerySection {
  id: number;
  tag: string;
  revision?: string | null;
  issued_date?: string | null;
  effective_date?: string | null;
  implementation_date?: string | null;
  paragraph_identifier?: string | null;
  requirement_text?: string | null;
  interpretative_guidelines: string[];
  survey_procedures: string[];
  created_at?: string;
  updated_at?: string;
  appendix_letter: string;
  file_path: string;
  country_code: string;
  industry: string;
  facility_type_id: number;
  facility_type_name: string;
  facility_type_slug: string;
}

export interface SomTagsQueryResponse {
  result: {
    count: number;
    sections: SomQuerySection[];
    lsc_k_tag_guidelines?: LscKTagGuidelineEntry[] | null;
  };
  http_status: number;
  tags_processed: number;
  tags_requested: string[];
}

function formatSomQueryErrorDetail(detail: unknown): string {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (typeof detail === "object" && detail !== null) {
    const o = detail as Record<string, unknown>;
    const msg = o.error_message;
    if (typeof msg === "string" && msg.trim()) return msg;
    try {
      return JSON.stringify(detail);
    } catch {
      return String(detail);
    }
  }
  return String(detail);
}

export async function querySomTagsByTag(params: {
  tag: string;
  country_code: string;
  industry: string;
  /** Required by API schema; rules server resolves aggregator via MCM. */
  org_name?: string;
}): Promise<SomTagsQueryResponse> {
  const url = `${getApiOrigin()}${SOM_TAGS_QUERY_ENDPOINT}`;
  const body = {
    tags: params.tag.trim(),
    country_code: params.country_code,
    industry: params.industry,
    org_name: params.org_name ?? "test",
  };

  const resp = await authenticatedFetch(url, {
    method: "POST",
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    let message = `${resp.status} ${resp.statusText}`;
    try {
      const data = (await resp.json()) as { detail?: unknown };
      const fromDetail = formatSomQueryErrorDetail(data?.detail);
      if (fromDetail) message = fromDetail;
    } catch {
      const text = await resp.text().catch(() => "");
      if (text) message = text;
    }
    throw new Error(message);
  }

  return (await resp.json()) as SomTagsQueryResponse;
}
